import React from 'react'
import { Button } from './Button'
import { useToast } from './Toast'

/**
 * One-time backup codes shown after MFA setup is confirmed. The API returns
 * these once and never again, so the user is prompted to save them before
 * continuing.
 */
export function BackupCodesPanel({
  codes,
  onContinue,
}: {
  codes: Array<string>
  onContinue: () => void
}) {
  const { showToast } = useToast()

  const [copied, setCopied] = React.useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'))
      showToast('Backup codes copied to clipboard', 'success')
      setCopied(true)
    } catch {
      /* clipboard blocked — the codes are still visible on screen */
    }
  }

  const handleDownload = () => {
    const blob = new Blob([`${codes.join('\n')}\n`], {
      type: 'text/plain',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'amliq-backup-codes.txt'
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
    showToast('Backup codes downloaded successfully', 'success')
  }

  return (
    <div className="flex flex-col gap-5" id="panel-backup-codes">
      <div className="flex flex-col gap-2">
        <h1 className="text-xl font-bold tracking-tight text-brand-dark-text">
          Save your backup codes
        </h1>
        <p className="text-sm leading-relaxed text-brand-copyright">
          Store these somewhere safe. Each code signs you in once if you lose access to your
          authenticator app.
        </p>
      </div>

      <div>
        <p className="text-sm font-semibold leading-relaxed">Your backup codes</p>
        <p className="text-sm leading-relaxed text-brand-copyright">
          Each code works once. Keep them private.
        </p>
        <ul className="grid grid-cols-2 gap-2 rounded-xl border border-brand-border-light bg-brand-bg-light p-4">
          {codes.map((code, index) => (
            <li
              key={index}
              className="text-center font-mono text-sm font-bold tracking-wider text-brand-dark-text"
            >
              {code}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex justify-between gap-3">
        <Button type="button" id="btn-backup-codes-copy" variant="outline" onClick={handleCopy}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button type="button" id="btn-download-codes" variant="outline" onClick={handleDownload}>
          Download
        </Button>
        <Button
          type="button"
          id="btn-backup-codes-continue"
          onClick={() => {
            showToast('Login success', 'success')
            setTimeout(() => {
              onContinue()
            }, 2000)
          }}
        >
          Continue
        </Button>
      </div>
    </div>
  )
}
